// src/components/EmployeeCredentialsPage.js

import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { getEmployeeCredentialsPath } from '../services/firestorePaths';
import { useLocation, useNavigate } from 'react-router-dom';
import { backTarget, carryFrom } from '../utils/backTarget';
import {
  ArrowLeft,
  Plus,
  Search,
  X,
  ShieldCheck,
  FileText,
  Stethoscope,
  Plane,
  ClipboardCheck,
  HeartPulse,
  Award,
  ChevronRight,
  Users,
  Filter,
} from 'lucide-react';

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

// Pure visual helper — just decides chip tone from raw value. Does not change data.
const valueTone = (v) => {
  if (!v || v === 'N/A')
    return {
      chip: 'bg-slate-400/10 text-slate-400 border-slate-400/20',
      label: 'N/A',
    };
  return {
    chip: 'bg-white/[0.05] text-slate-100 border-white/10',
    label: v,
  };
};

const EmployeeCredentialsPage = () => {
  const [credentials, setCredentials] = useState([]);
  const [filteredCredentials, setFilteredCredentials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  // Back: home when opened from the home page, else the Admin Dashboard. Passed
  // to an employee's page and back, so a round trip keeps it.
  const location = useLocation();
  const back = backTarget(location.state, '/dashboard', 'Back');
  const origin = carryFrom(location.state);

  useEffect(() => {
    const fetchCredentials = async () => {
      const querySnapshot = await getDocs(collection(db, getEmployeeCredentialsPath()));
      const creds = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      creds.sort((a, b) => {
        const lastNameA = a.NAME?.trim().split(' ').slice(-1)[0].toLowerCase();
        const lastNameB = b.NAME?.trim().split(' ').slice(-1)[0].toLowerCase();
        return lastNameA.localeCompare(lastNameB);
      });

      setCredentials(creds);
      setFilteredCredentials(creds);
    };

    fetchCredentials();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = credentials.filter((cred) =>
      cred.NAME?.toLowerCase().includes(query)
    );
    setFilteredCredentials(filtered);
  };

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(back.to)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
                <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.4} />
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                  Staff
                </div>
                <h1 className="text-base sm:text-lg font-semibold tracking-tight text-white leading-tight truncate">
                  Employee Credentials
                </h1>
              </div>
            </div>
          </div>

          <button
            onClick={() => navigate('/add-employee', { state: origin })}
            className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <Plus className="w-3.5 h-3.5 relative" />
            <span className="relative">Add Employee</span>
          </button>
        </div>

        {/* Search bar */}
        <section className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl px-2.5 py-2 mb-3 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by employee name…"
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pl-9 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilteredCredentials(credentials);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-xs text-slate-300 shrink-0">
              <Filter className="w-3.5 h-3.5 text-purple-300" />
              <span className="font-medium text-white">{filteredCredentials.length}</span>
              <span className="text-slate-400">of {credentials.length}</span>
            </div>
          </div>
        </section>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {filteredCredentials.length > 0 ? (
            filteredCredentials.map((cred) => {
              const fields = [
                { key: 'CV_EXP', label: 'CV', Icon: FileText, value: cred.CV_EXP },
                { key: 'GCP_EXP', label: 'GCP', Icon: ClipboardCheck, value: cred.GCP_EXP },
                { key: 'IATA_EXP', label: 'IATA', Icon: Plane, value: cred.IATA_EXP },
                { key: 'ML_EXP', label: 'Medical License', Icon: Stethoscope, value: cred.ML_EXP },
                { key: 'CPR_EXP', label: 'CPR', Icon: HeartPulse, value: cred.CPR_EXP },
                { key: 'CITI_EXP', label: 'CITI', Icon: Award, value: cred.CITI_EXP },
              ];
              return (
                <div
                  key={cred.id}
                  onClick={() => navigate(`/employee/${cred.id}`, { state: origin })}
                  className="group relative cursor-pointer rounded-2xl border border-white/10 bg-white/[0.035] backdrop-blur-xl overflow-hidden hover:border-purple-300/30 hover:shadow-[0_10px_40px_-10px_rgba(139,92,246,0.45)] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-400 opacity-60 group-hover:opacity-100 transition" />
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />

                  <div className="relative p-3 flex flex-col gap-2.5">
                    {/* Header */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-xs font-bold text-white ring-1 ring-white/20 shrink-0">
                          {(cred.NAME || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold leading-none">
                            Employee
                          </div>
                          <h3 className="text-sm font-semibold text-white truncate leading-tight">
                            {cred.NAME}
                          </h3>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-300 group-hover:translate-x-0.5 transition shrink-0" />
                    </div>

                    {/* Credential rows */}
                    <ul className="space-y-1.5 pt-1 border-t border-white/5">
                      {fields.map(({ key, label, Icon, value }) => {
                        const tone = valueTone(value);
                        return (
                          <li
                            key={key}
                            className="flex items-center justify-between gap-2"
                          >
                            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold min-w-0">
                              <Icon className="w-3 h-3 text-purple-300 shrink-0" />
                              <span className="truncate">{label}</span>
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono tracking-wider border ${tone.chip} shrink-0`}
                            >
                              {tone.label}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center">
              <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                <Users className="w-5 h-5 text-purple-300" />
              </div>
              <h3 className="text-sm font-semibold text-white">No matching employees</h3>
              <p className="text-xs text-slate-400 mt-1">
                Try adjusting your search.
              </p>
            </div>
          )}
        </div>

        <div className="h-6" />
      </div>
    </div>
  );
};

export default EmployeeCredentialsPage;
